import React, { useState, useEffect } from 'react'
import { Button, Form, Input, message, Dropdown, Menu, Modal } from 'antd'
import moment from 'moment'
import {
  ScheduleComponent,
  Day,
  Inject,
  CellClickEventArgs,
  PopupOpenEventArgs,
  EventRenderedArgs,
} from '@syncfusion/ej2-react-schedule'
import { useCreateScheduleTemplate, useAddActivitiesToSpecificTemplate, useRemoveActivityFromTemplate } from 'api/ScheduleTemplate/ScheduleTemplate'
import AppLayout from 'components/layout/layout'
import OrganizationInput from 'components/global/inputs/OrganizationInput/OrganizationInput'
import { CreateScheduleTemplateBody } from 'types/scheduleTemplate/scheduleTemplate'
import './TemplateCreator.scss'
import TemplateActivityTime, { TemplateTimeRange } from './modals/TemplateActivityTime'
import TemplateSelectActivityModal, {
  TemplateSelectedActivity,
} from './modals/TemplateSelectActivityModal'
import TemplateViewActivity from './modals/TemplateViewActivity'
import TemplateEditTime from './modals/TemplateEditTime'
import { Eye, ArrowsClockwise, Clock, Trash, List } from '@phosphor-icons/react'
import { RightOutlined } from '@ant-design/icons'
import { infoMessage } from 'api/global'

interface Template {
  _id?: string
  templateName: string
  description?: string
  organizationId: string
  organization?: {
    _id: string
    name: string
    email: string
    status: string
  }
  activities?: any[]
  tempActivities?: any[]
  createdAt?: string
  updatedAt?: string
}

interface TemplateCreatorProps {
  onClose: () => void
  template?: Template | null
}

interface cellDataType extends CellClickEventArgs {
  Id?: string
  eventActivityId?: string
  isDefault?: boolean
}

// Build the calendar's scheduleData from a template response. The backend
// regenerates every tempActivity subdocument _id on each add-activities call,
// so we always rebuild from the response to keep ids aligned (delete/edit rely
// on tempActivity._id).
const mapTemplateToScheduleData = (tpl: any): any[] => {
  const scheduled = tpl?.tempActivities || []
  const details = tpl?.activities || []
  return scheduled.map((sa: any) => {
    const detail = details.find((a: any) => a._id === sa.activityId)
    return {
      Id: sa._id,
      Subject: detail?.name || 'Activity',
      StartTime: new Date(sa.startTime),
      EndTime: new Date(sa.endTime),
      IsAllDay: false,
      eventActivityId: sa.activityId,
      isDefault: detail?.isDefault || false,
    }
  })
}

const TemplateCreator: React.FC<TemplateCreatorProps> = ({ onClose, template }) => {
  const [form] = Form.useForm()
  const [scheduleData, setScheduleData] = useState<any[]>([])
  const [timeModalOpen, setTimeModalOpen] = useState(false)
  const [selectActivityModal, setSelectActivityModal] = useState(false)
  const [cellData, setCellData] = useState<cellDataType>()
  const [loading, setLoading] = useState(false)
  const [savingActivity, setSavingActivity] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<any | undefined>(undefined)
  const [selectedDate] = useState(() => new Date())
  const [selectedTime, setSelectedTime] = useState<TemplateTimeRange | null>(null)
  const [templateId, setTemplateId] = useState<string | undefined>(template?._id)

  // Menu state variables
  const [menuVisible, setMenuVisible] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })
  const [clickedEvent, setClickedEvent] = useState<any>(null)
  const [menuKey, setMenuKey] = useState<string>()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [changeActivityModalOpen, setChangeActivityModalOpen] = useState(false)
  const [editTimeModalOpen, setEditTimeModalOpen] = useState(false)
  const [overlappingModalOpen, setOverlappingModalOpen] = useState(false)

  const { mutateAsync: createTemplate } = useCreateScheduleTemplate()
  const { mutateAsync: addActivities } = useAddActivitiesToSpecificTemplate()
  const { mutateAsync: removeActivity } = useRemoveActivityFromTemplate()

  useEffect(() => {
    if (template) {
      setTemplateId(template._id)
      form.setFieldsValue({
        templateName: template.templateName,
        description: template.description,
        organizationId: template.organizationId,
      })
      if (template.organizationId) {
        setSelectedOrganization({ 
          id: template.organizationId, 
          name: template.organization?.name || 'Selected Organization' 
        })
      }

      setScheduleData(mapTemplateToScheduleData(template))
    }
  }, [template, form])

  // Sync organization with form
  useEffect(() => {
    if (selectedOrganization?.id) {
      form.setFieldValue('organizationId', selectedOrganization.id)
    }
  }, [selectedOrganization, form])

  const onCellClick = (args: CellClickEventArgs) => {
    if (!selectedOrganization?.id) {
      infoMessage('Please select an organization first before selecting a time slot')
      args.cancel = true
      return
    }
    args.cancel = true
    setCellData(args)
    setTimeModalOpen(true)
  }

  const handlePopupOpen = (args: PopupOpenEventArgs) => {
    if (args.type === 'QuickInfo' || args.type === 'Editor') {
      args.cancel = true
    }
  }

  const onEventRendered = (args: EventRenderedArgs) => {
    const eventElement = args.element
    const event = args.data

    eventElement.style.backgroundColor =
      event.Subject === 'Check-in'
        ? '#FDEAD2'
        : event.Subject === 'Breakfast'
          ? '#D5E1F4'
          : event.Subject === 'Lunch'
            ? '#8CC73F33'
            : event.Subject === 'Dinner'
              ? '#D5EDF4'
              : event.Subject === 'Check-out'
                ? '#E8769F33'
                : '#47BFDF33'

    eventElement.style.color = '#ffffff'
    eventElement.style.borderRadius = '0px'
    eventElement.style.borderTop = '1px solid #fff'
    eventElement.style.borderBottom = '1px solid #fff'
    eventElement.classList.add('custom-event')

    eventElement.innerHTML = `
      <div class="custom-event-template">
        <div class="custom-event-header">${event.Subject}</div>
        <div class="custom-event-body">
          <div class="custom-event-time">
            ${new Date(event.StartTime).toLocaleTimeString()} - ${new Date(
      event.EndTime,
    ).toLocaleTimeString()}
          </div>
        </div>
        <div class="custom-menu-opener flex justify-center align-center">
            <div class="dot-container">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
        </div>
      </div>
    `

    eventElement.addEventListener('click', e => {
      e.preventDefault()
      e.stopPropagation()
    })

    const menuOpener = eventElement.querySelector('.custom-menu-opener')
    if (menuOpener) {
      menuOpener.addEventListener('click', e => {
        e.stopPropagation()
        handleMenuOpenerClick(event, e as any)
      })
    }
  }

  const handleTimeSave = (range: TemplateTimeRange) => {
    setSelectedTime(range)
    setTimeModalOpen(false)
    setSelectActivityModal(true)
  }

  // Menu handlers
  const handleMenuOpenerClick = (event: any, e: React.MouseEvent) => {
    e.preventDefault()
    const { clientX: x, clientY: y } = e
    setMenuPosition({ x, y })
    setClickedEvent(event)
    setMenuVisible(prev => !prev)
  }

  const handleMenuClick = (key: string) => {
    setMenuVisible(false)
    setMenuKey(key)
    switch (key) {
      case 'view-activity':
        setViewModalOpen(true)
        break
      case 'change-activity':
        setChangeActivityModalOpen(true)
        break
      case 'overlapping':
        setOverlappingModalOpen(true)
        // For overlapping activity, we want to open time modal with the same time slot
        if (clickedEvent) {
          const startTime = moment(clickedEvent.StartTime)
          const endTime = moment(clickedEvent.EndTime)
          setSelectedTime({ start: startTime, end: endTime })
          // Set the cell data with the clicked event's time for the time modal
          setCellData({
            StartTime: clickedEvent.StartTime,
            EndTime: clickedEvent.EndTime
          } as any)
          setTimeModalOpen(true)
        }
        break
      case 'edit-time':
        setEditTimeModalOpen(true)
        break
      case 'delete-activity':
        setDeleteModalOpen(true)
        break
      default:
        break
    }
  }

  const handleDeleteConfirm = async () => {
    if (!clickedEvent?.Id || !templateId || !selectedOrganization?.id) {
      message.error('Missing required information')
      return
    }
    try {
      const updated = await removeActivity({
        tempId: templateId,
        payload: {
          tempActivityId: clickedEvent.Id,
          organizationId: selectedOrganization.id,
        },
      })
      // Re-sync from the response so ids stay aligned with the backend.
      setScheduleData(mapTemplateToScheduleData(updated))
    } catch (error) {
      message.error('Failed to delete activity')
    } finally {
      setDeleteModalOpen(false)
    }
  }

  const handleEditTimeSave = async (range: TemplateTimeRange, eventId: string) => {
    if (!selectedOrganization?.id || !templateId) {
      message.error('Missing required information')
      return
    }

    try {
      const updatedScheduleData = scheduleData.map(item => {
        if (item.Id === eventId) {
          const today = new Date()
          const startTime = new Date(
            today.getFullYear(), today.getMonth(), today.getDate(),
            range.start.hour(), range.start.minute()
          )
          const endTime = new Date(
            today.getFullYear(), today.getMonth(), today.getDate(),
            range.end.hour(), range.end.minute()
          )
          return {
            ...item,
            StartTime: startTime,
            EndTime: endTime
          }
        }
        return item
      })

      // Update all activities in the template
      const activities = updatedScheduleData.map(item => ({
        isDefault: item.isDefault || false,
        startTime: item.StartTime.toISOString(),
        endTime: item.EndTime.toISOString(),
        activityId: item.eventActivityId
      }))

      const response = await addActivities({
        tempId: templateId,
        payload: {
          activities,
          organizationId: selectedOrganization.id
        }
      })

      // Rebuild from the response — add-activities regenerates every subdoc _id.
      setScheduleData(mapTemplateToScheduleData(response))
      setEditTimeModalOpen(false)
    } catch (error) {
      message.error('Failed to update activity time')
    }
  }

  const handleChangeActivity = async ({ activity, isDefault }: TemplateSelectedActivity) => {
    if (!clickedEvent || !selectedOrganization?.id || !templateId) {
      message.error('Missing required information')
      return
    }

    try {
      // Update the activity in the schedule data
      const updatedScheduleData = scheduleData.map(item => {
        if (item.Id === clickedEvent.Id) {
          return {
            ...item,
            Subject: activity.name,
            eventActivityId: activity._id,
            isDefault: isDefault
          }
        }
        return item
      })

      // Update all activities in the template
      const activities = updatedScheduleData.map(item => ({
        isDefault: item.isDefault || false,
        startTime: item.StartTime.toISOString(),
        endTime: item.EndTime.toISOString(),
        activityId: item.eventActivityId
      }))

      const response = await addActivities({
        tempId: templateId,
        payload: {
          activities,
          organizationId: selectedOrganization.id
        }
      })

      // Rebuild from the response — add-activities regenerates every subdoc _id.
      setScheduleData(mapTemplateToScheduleData(response))
      setChangeActivityModalOpen(false)
    } catch (error) {
      message.error('Failed to change activity')
    }
  }

  const handleActivityConfirm = async ({ activity, isDefault }: TemplateSelectedActivity) => {
    if (!selectedTime) return;
    if (!selectedOrganization?.id) {
      message.error('Please select an organization first');
      return;
    }
    let templateName: string | undefined;
    try {
      const values = await form.validateFields(['templateName', 'organizationId']);
      templateName = values?.templateName;
    } catch {
      return;
    }

    // 2. Prepare Date Strings
    const today = new Date();
    const startTime = new Date(
      today.getFullYear(), today.getMonth(), today.getDate(),
      selectedTime.start.hour(), selectedTime.start.minute()
    ).toISOString();

    const endTime = new Date(
      today.getFullYear(), today.getMonth(), today.getDate(),
      selectedTime.end.hour(), selectedTime.end.minute()
    ).toISOString();

    setSavingActivity(true);

    try {
      if (!templateId) {

        const body: CreateScheduleTemplateBody = {
          templateName: templateName || '',
          isDefault: isDefault,
          organizationId: selectedOrganization.id,
          activity: {
            startTime,
            endTime,
            activityId: activity._id,
          },
        };

        const created = await createTemplate({
          data: body,
          organizationId: selectedOrganization.id,
        });

        setTemplateId(created?._id);
        // Rebuild from the created template so the activity's Id is its real
        // tempActivity subdoc _id (not the template _id).
        setScheduleData(mapTemplateToScheduleData(created));
      } else {
        // Get current activities and add the new one
        const currentActivities = scheduleData.map(item => ({
          isDefault: item.isDefault || false,
          startTime: item.StartTime.toISOString(),
          endTime: item.EndTime.toISOString(),
          activityId: item.eventActivityId
        }));
        
        // Add the new activity
        const allActivities = [...currentActivities, { 
          isDefault, 
          startTime, 
          endTime, 
          activityId: activity._id 
        }];
        
        const response = await addActivities({
          tempId: templateId.toString(),
          payload: {
            activities: allActivities,
            organizationId: selectedOrganization.id
          },
        });

        // Rebuild the whole schedule from the response so every activity's Id
        // matches the backend's regenerated subdocument _ids.
        setScheduleData(mapTemplateToScheduleData(response));
      }

    } catch (error) {
      console.error(error);
    } finally {
      setSavingActivity(false);
      setSelectedTime(null);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true)
      await form.validateFields()

      if (!selectedOrganization?.id) {
        message.error('Please select an organization')
        return
      }

      if (!scheduleData.length) {
      infoMessage('Please add at least one activity to the template')
        return
      }
      onClose()
    } catch (error) {
      console.error('Validation error:', error)
    } finally {
      setLoading(false)
    }
  }

  const timeScale = { enable: true, interval: 60, slotCount: 4 }
  const eventSettings = { dataSource: scheduleData }
  
  return (
    <AppLayout>
      <div className="template-creator">
        <div className="template-creator-header">
          <div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}
                onClick={() => onClose()}
              >
                Schedule Templates
              </span>
              <RightOutlined /> {template ? 'Edit' : 'New'}
            </div>
          </div>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSave}
            className="save-btn"
          >
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </div>

        <div className="template-creator-content">
          <div className="template-form-section">
            <Form
              form={form}
              layout="vertical"
              className="template-form"
            >
              <Form.Item
                name="templateName"
                label={<span className="required-input">Template Name</span>}
                rules={[{ required: true, message: 'Please enter template name' }]}
              >
                <Input placeholder="Enter template name" style={{ marginBottom: '16px' }} />
              </Form.Item>

              <Form.Item
                name="organizationId"
                required
                rules={[
                  { required: true, message: 'Please select an organization' }
                ]}
              >
                <OrganizationInput
                  componentname='Template'
                  selectedOrganization={selectedOrganization}
                  setSelectedOrganization={setSelectedOrganization}
                />
              </Form.Item>
            </Form>
          </div>

          <div className="template-calendar-section">
            <div className="calendar-header">
              <h3>24-Hour Activity Schedule</h3>
              <p>Click on any time slot to add an activity</p>
            </div>

            <ScheduleComponent
              width="100%"
              height="850px"
              currentView="Day"
              showHeaderBar={false}
              cssClass="activity-scheduel-calendar"
              timeScale={timeScale}
              selectedDate={selectedDate}
              startHour="00:00"
              endHour="24:00"
              eventSettings={eventSettings}
              cellClick={onCellClick}
              popupOpen={handlePopupOpen}
              eventRendered={onEventRendered}
            >
              <Inject services={[Day]} />
            </ScheduleComponent>
          </div>
        </div>

        <TemplateActivityTime
          openModal={timeModalOpen}
          setOpenModal={setTimeModalOpen}
          defaultData={cellData as any}
          onSave={handleTimeSave}
        />

        {selectActivityModal && selectedOrganization?.id && (
          <TemplateSelectActivityModal
            modalOpen={selectActivityModal}
            setmodalopen={setSelectActivityModal}
            organizationId={selectedOrganization.id}
            onConfirm={handleActivityConfirm}
            loading={savingActivity}
          />
        )}

        {/* Activity Menu Dropdown */}
        {menuVisible && (
          <Dropdown
            overlay={
              <Menu onClick={({ key }) => handleMenuClick(key)}>
                <Menu.Item key="overlapping">
                  <div className="flex align-center gap-20px">
                    <List size={18} color="#000" />
                    <p>Add overlapping activity</p>
                  </div>
                </Menu.Item>
                <Menu.Item key="view-activity">
                  <div className="flex align-center gap-20px">
                    <Eye size={18} color="#000" />
                    <p>View activity</p>
                  </div>
                </Menu.Item>
                <Menu.Item key="change-activity">
                  <div className="flex align-center gap-20px">
                    <ArrowsClockwise size={18} color="#000" />
                    <p>Change activity</p>
                  </div>
                </Menu.Item>
                <Menu.Item key="edit-time">
                  <div className="flex align-center gap-20px">
                    <Clock size={18} color="#000" />
                    <p>Edit time</p>
                  </div>
                </Menu.Item>
                <Menu.Item key="delete-activity">
                  <div className="flex align-center gap-20px">
                    <Trash size={18} color="#FF6961" />
                    <p className="danger-txt">Delete activity</p>
                  </div>
                </Menu.Item>
              </Menu>
            }
            open={menuVisible}
            onOpenChange={setMenuVisible}
          >
            <div
              style={{
                position: 'absolute',
                left: menuPosition.x - 150,
                top: menuPosition.y - 20,
              }}
            >
              <Button type="text" />
            </div>
          </Dropdown>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <Modal
            title="Delete Activity"
            open={deleteModalOpen}
            onOk={handleDeleteConfirm}
            onCancel={() => setDeleteModalOpen(false)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            centered
          >
            <p>Are you sure you want to delete this activity?</p>
          </Modal>
        )}

        {/* View Activity Modal */}
        {viewModalOpen && clickedEvent && (
          <TemplateViewActivity
            modalOpen={viewModalOpen}
            modalClose={setViewModalOpen}
            type={clickedEvent.isDefault ? 'default' : 'specific'}
            activityId={clickedEvent.eventActivityId}
          />
        )}

        {/* Edit Time Modal */}
        {editTimeModalOpen && clickedEvent && (
          <TemplateEditTime
            openModal={editTimeModalOpen}
            setOpenModal={setEditTimeModalOpen}
            defaultData={clickedEvent}
            onSave={handleEditTimeSave}
          />
        )}

        {/* Change Activity Modal */}
        {changeActivityModalOpen && clickedEvent && selectedOrganization?.id && (
          <TemplateSelectActivityModal
            modalOpen={changeActivityModalOpen}
            setmodalopen={setChangeActivityModalOpen}
            organizationId={selectedOrganization.id}
            onConfirm={handleChangeActivity}
          />
        )}
      </div>
    </AppLayout>
  )
}

export default TemplateCreator
